import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Plus, Trash2, Settings, X, AlertCircle, Save, Edit } from "lucide-react";

const RULE_TPL = () => ({ distance_min: 0, distance_max: 30, oversized_min: 0, oversized_max: 1, package_name: "Forfait", price_ht: 100, on_quote: false });
const OPT_TPL = () => ({ id: Math.random().toString(36).slice(2), name: "Nouvelle option", description: "", type: "FIXE", amount: 10, auto_apply: false, condition_field: "", condition_operator: "eq", condition_value: "", unit_field: "", user_selectable: false, active: true });

const WEEKDAYS_FR = [
  { v: "monday", l: "Lundi" },
  { v: "tuesday", l: "Mardi" },
  { v: "wednesday", l: "Mercredi" },
  { v: "thursday", l: "Jeudi" },
  { v: "friday", l: "Vendredi" },
  { v: "saturday", l: "Samedi" },
  { v: "sunday", l: "Dimanche" },
];

const COND_FIELDS = [
  { v: "", l: "—" },
  { v: "delivery_weekday", l: "Jour de la semaine" },
  { v: "floor", l: "Étage" },
  { v: "elevator", l: "Ascenseur" },
  { v: "spiral_staircase", l: "Escalier colimaçon" },
  { v: "custom_slot", l: "Créneau imposé" },
  { v: "distance_km", l: "Distance (km)" },
  { v: "oversized_count", l: "Nb colis hors gabarit" },
];
const OPS = [
  { v: "eq", l: "= égal" },
  { v: "ne", l: "≠ différent" },
  { v: "gt", l: "> supérieur" },
  { v: "gte", l: "≥ sup. ou égal" },
  { v: "lt", l: "< inférieur" },
  { v: "lte", l: "≤ inf. ou égal" },
  { v: "in", l: "∈ dans la liste" },
  { v: "not_in", l: "∉ pas dans la liste" },
  { v: "truthy", l: "Coché / vrai" },
  { v: "falsy", l: "Décoché / faux" },
];

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/services").then((r) => setServices(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startNew = () => setEditing({
    name: "Nouveau service",
    description: "",
    active: true,
    tva_rate: 0.20,
    pricing_rules: [RULE_TPL()],
    options: [],
    allowed_weekdays: ["monday", "thursday"],
    non_standard_day_surcharge_ht: 30,
  });

  const startEdit = (s) => setEditing(JSON.parse(JSON.stringify(s)));

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      // strip id from rules/options (rules have no id, options keep id)
      const payload = {
        name: editing.name,
        description: editing.description,
        active: editing.active,
        tva_rate: Number(editing.tva_rate) || 0.20,
        allowed_weekdays: editing.allowed_weekdays || ["monday", "thursday"],
        non_standard_day_surcharge_ht: Number(editing.non_standard_day_surcharge_ht) || 0,
        pricing_rules: editing.pricing_rules.map((r) => ({
          ...r,
          distance_min: Number(r.distance_min) || 0,
          distance_max: r.distance_max === "" || r.distance_max === null ? null : Number(r.distance_max),
          oversized_min: parseInt(r.oversized_min) || 0,
          oversized_max: r.oversized_max === "" || r.oversized_max === null ? null : parseInt(r.oversized_max),
          price_ht: Number(r.price_ht) || 0,
          on_quote: !!r.on_quote,
        })),
        options: editing.options,
      };
      if (editing.id) {
        await api.put(`/admin/services/${editing.id}`, payload);
      } else {
        await api.post("/admin/services", payload);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeService = async (s) => {
    if (!window.confirm(`Supprimer le service "${s.name}" ?`)) return;
    await api.delete(`/admin/services/${s.id}`);
    load();
  };

  return (
    <div data-testid="admin-services-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="lc-small-label mb-1">Back-office</div>
          <h1 className="display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <Settings className="w-7 h-7 text-[var(--lc-orange)]" />
            Services &amp; Tarifs
          </h1>
        </div>
        <button onClick={startNew} data-testid="admin-add-service" className="lc-btn-primary inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nouveau service
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading && <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>}

      <div className="grid gap-3" data-testid="services-list">
        {services.map((s) => (
          <div key={s.id} data-testid={`service-row-${s.id}`} className="lc-card p-4 sm:p-5 flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold">{s.name} <span className="text-xs text-[var(--lc-gray)] ml-2">{s.active ? "Actif" : "Inactif"}</span></div>
              <div className="text-xs text-[var(--lc-gray)] mt-1">{s.pricing_rules?.length || 0} règles tarifaires · {s.options?.length || 0} options</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(s)} data-testid={`service-edit-${s.id}`} className="px-3 py-1.5 text-sm font-semibold border border-[var(--lc-border)] rounded hover:border-[var(--lc-black)] inline-flex items-center gap-1.5">
                <Edit className="w-3.5 h-3.5" /> Modifier
              </button>
              <button onClick={() => removeService(s)} data-testid={`service-delete-${s.id}`} className="px-3 py-1.5 text-sm font-semibold border border-red-300 text-red-600 rounded hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ServiceEditor editing={editing} setEditing={setEditing} onClose={() => setEditing(null)} onSave={save} saving={saving} />
      )}
    </div>
  );
}

function ServiceEditor({ editing, setEditing, onClose, onSave, saving }) {
  const upd = (k, v) => setEditing((s) => ({ ...s, [k]: v }));
  const updRule = (i, k, v) => setEditing((s) => {
    const arr = [...s.pricing_rules]; arr[i] = { ...arr[i], [k]: v };
    return { ...s, pricing_rules: arr };
  });
  const addRule = () => setEditing((s) => ({ ...s, pricing_rules: [...s.pricing_rules, RULE_TPL()] }));
  const delRule = (i) => setEditing((s) => ({ ...s, pricing_rules: s.pricing_rules.filter((_, idx) => idx !== i) }));
  const updOpt = (i, k, v) => setEditing((s) => {
    const arr = [...s.options]; arr[i] = { ...arr[i], [k]: v };
    return { ...s, options: arr };
  });
  const addOpt = () => setEditing((s) => ({ ...s, options: [...s.options, OPT_TPL()] }));
  const delOpt = (i) => setEditing((s) => ({ ...s, options: s.options.filter((_, idx) => idx !== i) }));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch md:items-center justify-center p-0 md:p-6" data-testid="service-editor">
      <div className="bg-white w-full md:max-w-4xl md:max-h-[90vh] overflow-auto md:rounded-md">
        <div className="sticky top-0 bg-white border-b border-[var(--lc-border)] px-5 py-3 flex items-center justify-between z-10">
          <div className="font-bold">{editing.id ? "Modifier le service" : "Nouveau service"}</div>
          <button onClick={onClose} data-testid="service-editor-close" className="p-2 hover:bg-black/5 rounded"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="lc-label">Nom</label>
              <input data-testid="service-name" className="lc-input" value={editing.name} onChange={(e) => upd("name", e.target.value)} />
            </div>
            <div>
              <label className="lc-label">TVA (ex: 0.20)</label>
              <input data-testid="service-tva" type="number" step="0.01" className="lc-input" value={editing.tva_rate} onChange={(e) => upd("tva_rate", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="lc-label">Description</label>
              <textarea rows="2" data-testid="service-description" className="lc-input" value={editing.description || ""} onChange={(e) => upd("description", e.target.value)} />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="service-active" type="checkbox" checked={editing.active} onChange={(e) => upd("active", e.target.checked)} />
                Service actif (visible côté client)
              </label>
            </div>
          </div>

          {/* Allowed weekdays */}
          <div>
            <div className="lc-small-label mb-2">Jours de livraison autorisés</div>
            <p className="text-xs text-[var(--lc-gray)] mb-3">Les jours non cochés ne seront pas sélectionnables par le client. Si le client choisit malgré tout un jour autorisé exceptionnellement, le supplément ci-dessous est appliqué.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {WEEKDAYS_FR.map((d) => {
                const isOn = (editing.allowed_weekdays || []).includes(d.v);
                return (
                  <button
                    key={d.v}
                    type="button"
                    data-testid={`weekday-toggle-${d.v}`}
                    onClick={() => upd(
                      "allowed_weekdays",
                      isOn
                        ? (editing.allowed_weekdays || []).filter((x) => x !== d.v)
                        : [...(editing.allowed_weekdays || []), d.v]
                    )}
                    className={`px-2.5 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                      isOn
                        ? "bg-[var(--lc-orange)] text-white border-[var(--lc-orange)]"
                        : "bg-white text-[var(--lc-dark-gray)] border-[var(--lc-border)] hover:border-[var(--lc-black)]"
                    }`}
                  >
                    {d.l}
                  </button>
                );
              })}
            </div>
            <div className="max-w-xs">
              <label className="lc-label text-[11px]">Supplément (HT) pour jour exceptionnel</label>
              <input
                data-testid="non-standard-day-surcharge"
                type="number" min="0" step="1"
                className="lc-input"
                value={editing.non_standard_day_surcharge_ht ?? 0}
                onChange={(e) => upd("non_standard_day_surcharge_ht", Number(e.target.value) || 0)}
              />
            </div>
          </div>

          {/* Pricing rules */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="lc-small-label">Règles tarifaires (matrice distance × hors gabarit)</div>
              <button onClick={addRule} data-testid="add-pricing-rule" className="text-xs font-semibold text-[var(--lc-orange)] inline-flex items-center gap-1"><Plus className="w-3 h-3" />Ajouter</button>
            </div>
            <p className="text-xs text-[var(--lc-gray)] mb-2">Logique : on retient la règle de prix le plus élevé qui correspond à la fois à la distance et au nb de colis hors gabarit.</p>
            <div className="space-y-2">
              {editing.pricing_rules.map((r, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-7 gap-2 items-end bg-[var(--lc-surface)] p-3 rounded">
                  <div>
                    <label className="lc-label text-[10px]">Dist. min (km)</label>
                    <input type="number" min="0" className="lc-input py-2" value={r.distance_min} onChange={(e) => updRule(i, "distance_min", e.target.value)} />
                  </div>
                  <div>
                    <label className="lc-label text-[10px]">Dist. max (km)</label>
                    <input type="number" min="0" className="lc-input py-2" value={r.distance_max ?? ""} placeholder="∞" onChange={(e) => updRule(i, "distance_max", e.target.value === "" ? null : e.target.value)} />
                  </div>
                  <div>
                    <label className="lc-label text-[10px]">HG min</label>
                    <input type="number" min="0" className="lc-input py-2" value={r.oversized_min} onChange={(e) => updRule(i, "oversized_min", e.target.value)} />
                  </div>
                  <div>
                    <label className="lc-label text-[10px]">HG max</label>
                    <input type="number" min="0" className="lc-input py-2" value={r.oversized_max ?? ""} placeholder="∞" onChange={(e) => updRule(i, "oversized_max", e.target.value === "" ? null : e.target.value)} />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="lc-label text-[10px]">Forfait</label>
                    <input className="lc-input py-2" value={r.package_name} onChange={(e) => updRule(i, "package_name", e.target.value)} />
                  </div>
                  <div>
                    <label className="lc-label text-[10px]">Prix HT (€)</label>
                    <input type="number" min="0" step="1" className="lc-input py-2" value={r.price_ht} onChange={(e) => updRule(i, "price_ht", e.target.value)} />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] uppercase flex items-center gap-1">
                      <input type="checkbox" checked={!!r.on_quote} onChange={(e) => updRule(i, "on_quote", e.target.checked)} /> Devis
                    </label>
                    <button onClick={() => delRule(i)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="lc-small-label">Options &amp; suppléments</div>
              <button onClick={addOpt} data-testid="add-option" className="text-xs font-semibold text-[var(--lc-orange)] inline-flex items-center gap-1"><Plus className="w-3 h-3" />Ajouter</button>
            </div>
            <div className="space-y-3">
              {editing.options.map((o, i) => (
                <div key={i} className="border border-[var(--lc-border)] rounded p-3 grid sm:grid-cols-12 gap-2 bg-white">
                  <div className="sm:col-span-3"><label className="lc-label text-[10px]">Nom</label><input className="lc-input py-2" value={o.name} onChange={(e) => updOpt(i, "name", e.target.value)} /></div>
                  <div className="sm:col-span-2">
                    <label className="lc-label text-[10px]">Type</label>
                    <select className="lc-input py-2" value={o.type} onChange={(e) => updOpt(i, "type", e.target.value)}>
                      <option value="FIXE">Montant fixe</option>
                      <option value="PAR_UNITE">Par unité</option>
                    </select>
                  </div>
                  <div className="sm:col-span-1"><label className="lc-label text-[10px]">€</label><input type="number" className="lc-input py-2" value={o.amount} onChange={(e) => updOpt(i, "amount", Number(e.target.value))} /></div>
                  <div className="sm:col-span-2">
                    <label className="lc-label text-[10px]">Auto si</label>
                    <select className="lc-input py-2" value={o.condition_field || ""} onChange={(e) => updOpt(i, "condition_field", e.target.value)}>
                      {COND_FIELDS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="lc-label text-[10px]">Opérateur</label>
                    <select className="lc-input py-2" value={o.condition_operator || "eq"} onChange={(e) => updOpt(i, "condition_operator", e.target.value)}>
                      {OPS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="lc-label text-[10px]">Valeur</label>
                    <input className="lc-input py-2" value={o.condition_value ?? ""} onChange={(e) => updOpt(i, "condition_value", e.target.value)} placeholder="ex: saturday, 0, [monday,thursday]" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="lc-label text-[10px]">Champ d'unité (si PAR_UNITE)</label>
                    <input className="lc-input py-2" value={o.unit_field || ""} onChange={(e) => updOpt(i, "unit_field", e.target.value)} placeholder="floor" />
                  </div>
                  <div className="sm:col-span-2 flex items-center gap-3 pt-3 sm:pt-0">
                    <label className="text-[11px] flex items-center gap-1"><input type="checkbox" checked={!!o.auto_apply} onChange={(e) => updOpt(i, "auto_apply", e.target.checked)} />Auto</label>
                    <label className="text-[11px] flex items-center gap-1"><input type="checkbox" checked={!!o.user_selectable} onChange={(e) => updOpt(i, "user_selectable", e.target.checked)} />Choix client</label>
                    <label className="text-[11px] flex items-center gap-1"><input type="checkbox" checked={o.active !== false} onChange={(e) => updOpt(i, "active", e.target.checked)} />Actif</label>
                  </div>
                  <div className="sm:col-span-12 flex justify-end">
                    <button onClick={() => delOpt(i)} className="text-red-600 text-xs inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Supprimer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[var(--lc-border)] px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="lc-btn-secondary">Annuler</button>
          <button onClick={onSave} disabled={saving} data-testid="service-save-button" className="lc-btn-primary inline-flex items-center gap-2"><Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}
